//@ts-strict-ignore
import { AfterViewChecked, AfterViewInit, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { LMContextService } from '@services/lm-context.service';
import { LMMenuSecurityService } from '@components/menusecurity/lm-menu-security.service';
import { SohoBusyIndicatorDirective, SohoDataGridComponent, SohoListViewComponent } from 'ids-enterprise-ng';
import { LMBusyIndicatorService, LMErrorUtilService } from '@app/services';
import { LMListDatagridWrapperComponent } from '@components/list/lm-list-datagrid-wrapper.component';

@Component({
  selector: 'lm-menu-security',
  standalone: false,
  templateUrl: 'lm-menu-security.component.html',
  styleUrls: [ 'lm-menu-security.component.scss' ],
  })

export class LMMenuSecurityComponent implements OnInit, AfterViewInit, AfterViewChecked, OnDestroy {

  @ViewChild(SohoBusyIndicatorDirective) busyIndicator: SohoBusyIndicatorDirective;
  @ViewChild('DatagridWrapper') datagridWrapper: LMListDatagridWrapperComponent;
  @ViewChild('SohoListView_Items') sohoListViewComponent_Items: SohoListViewComponent;

  get dataGridComponent(): SohoDataGridComponent {
    return this.datagridWrapper?.sohoDataGridComponent;
  }

  @Input() options: LMMenuSecurityRequest;

  webAppComponents: Array<LMWebAppComponent>;
  activeRow = 0;
  flattenData: Array<LMWebAppComponentItem>
  gridOptions: SohoDataGridOptions;
  dataView: LMDataView;
  notAccessibleItems: LMNotAccessibleItems;
  notAccessibleItemsKeys: Array<string>;
  busyTimeOut: number;
  validateChildrenStateOnClose = false;

  constructor(
    protected lmContextService: LMContextService,
    protected lmMenuSecurityService: LMMenuSecurityService,
    protected lmBusyIndicatorService: LMBusyIndicatorService,
    protected lmErrorUtilService: LMErrorUtilService) {
  }

  ngOnInit(): void {
    this.buildMenuItemDataGrid();
    this.busyTimeOut = this.lmContextService.uiLoadingIndicatorTimeout;
  }

  ngAfterViewInit(): void {
    this.loadData();
  }

  ngAfterViewChecked() {
  }

  ngOnDestroy(): void {
  }

  private loadData() {

    this.lmBusyIndicatorService.showBusyIndicator(this.busyIndicator);

    this.lmMenuSecurityService.getMenuSecurityData(this.options).subscribe(response => {

      if (response?.status === 'SUCCESS') {
        this.webAppComponents = response.menus;
        this.notAccessibleItems = response.notAccessibleItems;
        this.notAccessibleItemsKeys = Object.keys(this.notAccessibleItems);
        this.setInaccessibleItemState();
        this.dataView = response.dataView;
        this.setTreeState(this.webAppComponents);
        this.lmBusyIndicatorService.hideBusyIndicator(this.busyIndicator);
        setTimeout(() => this.sohoListViewComponent_Items.select(0));
      } else {
        this.lmErrorUtilService.handleUnexpectedError(response);
      }
    });
  }

  private buildMenuItemDataGrid() {
    const columns: SohoDataGridColumn[] = [
      {
        id:             'name',
        name:           Soho.Locale.translate('Name'),
        field:          'name',
        singleline:     true,
        sortable:       false,
        filterType:     'text',
        filterConditions: ['contains'],
        formatter:      Soho.Formatters.Tree,
      },
      {
        id:             'secured',
        name:           Soho.Locale.translate('NotAccessible'),
        field:          'notAccessible',
        sortable:       false,
        filterType:     'select',
        options:        [ { label: Soho.Locale.translate('Yes'), value: true }, { label: Soho.Locale.translate('No'), value: false } ],
        singleline:     true,
        isEditable:     (row: number, cell: any, fieldValue: any, columnDef: SohoDataGridColumn, rowData: any): boolean =>  this.isEditable(row, cell, fieldValue, columnDef, rowData),
        align:          'center',
        formatter:      Soho.Formatters.Checkbox,
        editor:         Soho.Editors.Checkbox,
        tooltip:        (row: number, cell: number, value: any, col: SohoDataGridColumn, rowData: any, api: SohoDataGridStatic): string => this.cellToolTip(row, cell, value, col, rowData, api),
      },
      {
        id:             'hasSuggestion',
        name:           Soho.Locale.translate('HasSuggestion'),
        field:          'hasSuggestion',
        sortable:       false,
        filterType:     'select',
        options:        [ { label: Soho.Locale.translate('Yes'), value: true }, { label: Soho.Locale.translate('No'), value: false } ],
        singleline:     true,
        align:          'center',
        formatter:      this.DataGridTargetFormatter,
      },
      {
        id:             'propertyId',
        name:           'Property Id', //Soho.Locale.translate('VisibleWhen'),
        field:          'propertyId',
        sortable:       false,
        filterType:     'text',
        filterConditions: ['contains'],
        singleline:     true,
        align:          'left',
        formatter:      Soho.Formatters.Ellipsis,
      },
      {
        id:             'validity',
        name:           Soho.Locale.translate('ValidWhen'),
        field:          'validity',
        sortable:       false,
        singleline:     true,
        align:          'left',
        formatter:      Soho.Formatters.Ellipsis,
      },
      {
        id:             'visibility',
        name:           Soho.Locale.translate('VisibleWhen'),
        field:          'visibility',
        sortable:       false,
        singleline:     true,
        align:          'left',
        formatter:      Soho.Formatters.Ellipsis,
      },
      {
        id:             'targetType',
        name:           Soho.Locale.translate('Type'),
        field:          'target.targetType',
        sortable:       false,
        singleline:     true,
        align:          'left',
        filterType:     'text',
        filterConditions: ['contains'],
        formatter:      this.DataGridTargetFormatter,
      },
      {
        id:             'target',
        name:           Soho.Locale.translate('Name'),
        field:          'target.name',
        sortable:       false,
        singleline:     true,
        align:          'left',
        filterType:     'text',
        filterConditions: ['contains'],
        formatter:      this.DataGridTargetFormatter,
      },
      {
        id:             'info',
        name:           '',
        sortable:       false,
        singleline:     true,
        click:          this.DataGridColumnClick,
        formatter:      this.DataGridLinkFormatter,
      } ];


    const columnGroups = [
      { colspan: 1, id: 'grp1', name: '', align: 'center' },
      { colspan: 1, id: 'grp1', name: '', align: 'center' },
      { colspan: 1, id: 'grp1', name: '', align: 'center' },
      { colspan: 1, id: 'grp1', name: '', align: 'center' },
      { colspan: 1, id: 'grp1', name: '', align: 'center' },
      { colspan: 1, id: 'grp1', name: '', align: 'center' },
      { colspan: 2, id: 'targetGroup', name: Soho.Locale.translate('Target'), align: 'center' },
      { colspan: 1, id: 'grp1', name: '', align: 'center' },
    ];

    this.gridOptions = <SohoDataGridOptions>{
      columns,
      columnGroups,
      selectable:             'mixed',
      enableTooltips:         true,
      frozenColumns:          { left: ['name'] },
      editable:               true,
      filterable:             false,  // soho has issue editing a filtered grid.
      paging:                 false,
      treeGrid:               true,
      showDirty:              false,
      rowHeight:              'short',
      clickToSelect:          true,
      headerBackgroundColor:  'light',
      emptyMessage:           { title: Soho.Locale.translate('NoWebApplicationSearchableItemsFound') }
    };
  }

  onUIChange() {
    setTimeout(() => this.loadData());
  }

  onRowActivated(event: SohoDataGridRowActivatedEvent) {
    if (!event) {
      return;
    }
    this.activeRow = event.row;
  }

  onPropertiesChange(event: SohoDataGridCellChangeEvent) {
    if (!event) {
      return;
    }

    const row = event.row;
    const secure = event.value;
    const data = this.flattenData[row];

    if (secure) {
      this.notAccessibleItems[data.propertyId] = true;
    } else {
      delete this.notAccessibleItems[data.propertyId];
    }
    this.updateChildrenState(data, secure);
    this.notAccessibleItemsKeys = Object.keys(this.notAccessibleItems);

    this.dataGridComponent.updated();
  }

  onWebAppComponentSelected(event: any) {
    const rows: LMListRowData[] = event[1].selectedItems.map(elem => {
      const idx = elem.index();
      const data = this.webAppComponents[idx];
      return <LMListRowData>{ idx, data, elem };
    });

    const menu: LMWebAppComponent = rows[0].data;
    this.dataGridComponent.clearFilter();
    this.dataGridComponent.dataset = [].concat(menu.children);
    this.dataGridComponent.activateRow(0);
    this.flatten(menu.children);
  }

  onComponentInfoClick(webAppComponent: LMWebAppComponent) {
    this.lmMenuSecurityService.openWebAppComponentInfoDialog(webAppComponent);
  }

  onInaccessibleItemRemove(item: string) {
    this.validateChildrenStateOnClose = true;
    delete this.notAccessibleItems[item];

    this.webAppComponents.forEach(component => {
      this.updateInaccessible(item, component.children);
    });

    this.notAccessibleItemsKeys = Object.keys(this.notAccessibleItems);
    this.dataGridComponent.updated();
  }

  private buildInaccessibleItems(properties: Array<string>) {
    this.webAppComponents.forEach(wac => {
      this._checkChildren(undefined, wac.children, properties);
    });
  }

  private _checkChildren(parent: LMWebAppComponentItem, children: Array<LMWebAppComponentItem>, properties: Array<string>) {
    if (children) {
      children.forEach(child => {
        if (parent?.notAccessible) {
          child.notAccessible = true;
        }
        if (child.notAccessible) {
          properties.push(child.propertyId);
        }
        this._checkChildren(child, child.children, properties);
      });
    }
  }

  public getDataView() {
    const field = this.dataView.fields['ParamConfiguration'];
    let keys;

    if (this.validateChildrenStateOnClose) {
      const sb: Array<string> = [];
      this.buildInaccessibleItems(sb);
      keys = Object.keys(this.notAccessibleItems);

      sb.forEach(s => {
        if (!keys.includes(s)) {
          keys.push(s);
        }
      });
    } else {
      keys = Object.keys(this.notAccessibleItems);
    }

    keys.sort();
    const sb1: Array<string> = [];

    keys.forEach(k => {
      sb1.push(k);
    });
    field.value = sb1.join('\n');

    return this.dataView;
  }

  private updateInaccessible(itemKey: string, items: Array<LMWebAppComponentItem>){
    if (items) {
      items.forEach(item => {
        if (item.propertyId === itemKey) {
          item.notAccessible = false;
          this.updateChildrenState(item, false);
          return;
        }
        this.updateInaccessible(itemKey, item.children);
      });
    };
  }

  private setInaccessibleItemState() {
    this.notAccessibleItemsKeys.forEach(propertyId => {
      this.validInaccessibleItem(propertyId);
    });
  }

  private validInaccessibleItem(propertyId: string) {
    if (this.webAppComponents) {
      this.webAppComponents.forEach(component => {
        this._validInaccessibleItem(propertyId, component.children);
      });
    }
  }

  private _validInaccessibleItem(propertyId: string, items: Array<LMWebAppComponentItem>) {
    if (items) {
      for (const item of items) {
        if (item.propertyId === propertyId) {
          this.notAccessibleItems[propertyId] = true;
        }
        this._validInaccessibleItem(propertyId, item.children);
      }
    }
  }

  private flatten(items: Array<LMWebAppComponentItem | LMWebAppComponent>) {
    const list = [];
    this._flatten(items, list);
    this.flattenData = list;
  }

  private _flatten(children: Array<LMWebAppComponentItem | LMWebAppComponent>, flatList: Array<LMWebAppComponentItem | LMWebAppComponent>) {
    if (children) {
      children.forEach((item) => {
        flatList.push(item);
        this._flatten(item.children, flatList);
      });
    }
  }

  private updateChildrenState(item: LMWebAppComponentItem, secure: boolean) {
    if (item.children) {
      for (const child of item.children) {
        child.disabled = secure;

        if (secure) {
          child.notAccessible = secure;
          this.notAccessibleItems[child.propertyId] = true;
        } else {
          child.notAccessible = secure;
          delete this.notAccessibleItems[child.propertyId];
        }
         this.updateChildrenState(child, secure);
      }
    }
  }

  private isEditable(row: number, cell: any, fieldValue: any, columnDef: SohoDataGridColumn, rowData: LMWebAppComponentItem): boolean {
    if (rowData.notApplicable)
      return false;

    return !rowData.disabled;
  }

  private cellToolTip(row: number, cell: number, value: any, col: SohoDataGridColumn, rowData: LMWebAppComponentItem, api: SohoDataGridStatic): string {
    if (rowData.notApplicable)
      return Soho.Locale.translate('NotApplicableToolTip');

    return undefined;
  }

  private setTreeState(menus: LMWebAppComponent[]) {
    for (const menu of menus) {
      if (menu.children) {
        this._setTreeState(menu.children, null);
      }
    }
  }

  private _setTreeState(items: LMWebAppComponentItem[], parent: LMWebAppComponentItem) {
    for (const item of items) {
      item.expanded = false;
      item.disabled = parent?.notAccessible || parent?.disabled;
      if (item.children) {
        this._setTreeState(item.children, item);
      }
    }
  }

  DataGridTargetFormatter = (row: number, cell: number, value: any, dataGridColumn: SohoDataGridColumn, data: LMWebAppComponentItem, dataGrid: SohoDataGridStatic): string =>
    this.targetFormatter(row, cell, value, dataGridColumn, data, dataGrid);

  DataGridLinkFormatter = (row: number, cell: number, value: any, dataGridColumn: SohoDataGridColumn, data: LMWebAppComponentTarget, dataGrid: SohoDataGridStatic): string =>
    this.hyperLinkFormatter(row, cell, value, dataGridColumn, data, dataGrid);

  DataGridColumnClick = (e: Event, args: SohoDataGridColumnClickData[]) => this.onGridDetailClick(e, args);

  private onGridDetailClick = (
    e: Event,
    args: SohoDataGridColumnClickData[]
  ) => {
    if (args?.length > 0) {
      this.lmMenuSecurityService.openWebAppComponentItemInfoDialog(args[0].item);
    }
  }

  private targetFormatter = (
    row: number,
    cell: number,
    value: any,
    dataGridColumn: SohoDataGridColumn,
    data: LMWebAppComponentItem,
    dataGrid: SohoDataGridStatic
  ): string => {
    if (dataGridColumn.id === 'target') {
      if (data.target.businessView) {
        return Soho.Formatters.Ellipsis(row, cell, data.target.businessView + '.' + data.target.name, dataGridColumn, data, dataGrid);
      } else {
        return Soho.Formatters.Ellipsis(row, cell, data.target.name, dataGridColumn, data, dataGrid);
      }
    } else if (dataGridColumn.id === 'hasSuggestion') {
      if (data.hasSuggestion) {
        return data.suggestion === 'inaccessible' ? 'Hide' : 'Show';
      }
      return '';
    } else {
      const translation = Soho.Locale.translate(value);
      return  Soho.Formatters.Ellipsis(row, cell, translation, dataGridColumn, data, dataGrid);
    }
  }

  private hyperLinkFormatter = (
    row: number,
    cell: number,
    value: any,
    dataGridColumn: SohoDataGridColumn,
    data: LMWebAppComponentTarget,
    dataGrid: SohoDataGridStatic
  ): string =>
    Soho.Formatters.Hyperlink(row, cell, Soho.Locale.translate(Soho.Locale.translate('Detail')), dataGridColumn, data, dataGrid)
}

